'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { FreeToolsShowcase } from './FreeToolsShowcase';
import {
  Globe,
  FileText,
  Search,
  Mail,
  Gauge,
  DollarSign,
  Smartphone, 
  Video, 
  PenTool, 
  MapPin, 
  QrCode, 
  Home, 
  Send, 
  Users, 
  Palette, 
  Rocket,
  ArrowRight,
  Monitor,
  Target,
  Bot,
  User,
  Building,
  Shield,
  TrendingUp,
  Star,
  Lightbulb,
  Library,
  Briefcase,
  Diamond,
  GraduationCap
} from '@/lib/icons';

interface MegaMenuItem {
  label: string;
  href: string;
  description: string;
  icon?: string;
}

interface MegaMenuColumn {
  title: string;
  items: MegaMenuItem[];
}

interface MegaMenuCTA {
  icon?: string;
  headline: string;
  subtext: string;
  href: string;
  buttonText: string;
}

interface MegaMenuDropdownProps {
  columns: MegaMenuColumn[];
  cta?: MegaMenuCTA;
  onItemClick?: () => void;
  position: {
    left?: string;
    right?: string;
    transform?: string;
    visibility: 'visible' | 'hidden';
  };
}

const iconMap = {
  'Globe': Globe,
  'FileText': FileText,
  'Search': Search,
  'Mail': Mail,
  'DollarSign': DollarSign,
  'Gauge': Gauge,
  'Smartphone': Smartphone,
  'Video': Video,
  'PenTool': PenTool,
  'MapPin': MapPin,
  'QrCode': QrCode,
  'Home': Home,
  'Send': Send,
  'Users': Users,
  'Palette': Palette,
  'Rocket': Rocket,
  'ArrowRight': ArrowRight,
  'Monitor': Monitor,
  'Target': Target,
  'Bot': Bot,
  'User': User,
  'Building': Building,
  'Shield': Shield,
  'TrendingUp': TrendingUp,
  'Star': Star,
  'Lightbulb': Lightbulb,
  'Library': Library,
  'Briefcase': Briefcase,
  'Diamond': Diamond,
  'GraduationCap': GraduationCap,
  'Calculator': DollarSign, // Using DollarSign for Calculator
  'Download': FileText // Using FileText for Guide
};

interface PremiumTile {
  title: string;
  description: string;
  href: string;
  ctaText: string;
  icon: string;
  gradient: string;
  textColor: string;
  badge: string;
}

const PREMIUM_TILES: PremiumTile[] = [
  {
    title: "Mortgage Website Grader",
    description: "Score your SEO, trust signals, and AI visibility",
    href: "/mortgage-website-grader",
    ctaText: "Grade My Site",
    icon: "Gauge",
    gradient: "from-cyan-500 to-blue-600",
    textColor: "text-cyan-600 hover:text-cyan-700",
    badge: "Free - Can AI find your site?"
  },
  {
    title: "Marketing ROI Calculator",
    description: "Calculate your mortgage marketing ROI",
    href: "/mortgage-marketing-roi-calculator",
    ctaText: "Calculate ROI",
    icon: "Calculator",
    gradient: "from-emerald-500 to-teal-600",
    textColor: "text-emerald-600 hover:text-emerald-700",
    badge: "See your real cost per funded loan"
  },
  {
    title: "Lead Generation Guide",
    description: "The Mortgage Lead Generation Apocalypse",
    href: "/guide/mortgage-lead-generation-apocalypse",
    ctaText: "Download Guide",
    icon: "Download",
    gradient: "from-purple-500 to-indigo-600",
    textColor: "text-purple-600 hover:text-purple-700",
    badge: "Downloaded 25,000+ times"
  }
];

export function MegaMenuDropdown({ columns, cta, onItemClick, position }: MegaMenuDropdownProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [previousTabIndex, setPreviousTabIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleItemClick = () => {
    setIsClosing(true);
    setTimeout(() => {
      onItemClick?.();
    }, 150);
  };

  const handleTabClick = (index: number) => {
    if (index !== activeTabIndex) {
      setPreviousTabIndex(activeTabIndex);
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTabIndex(index);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 150);
    }
  };

  const activeColumn = columns[activeTabIndex] || columns[0];
  // The Free Tools menu lists every tool once in its own list: the generic showcase tiles would repeat the same tools (owner 2026-10-08)
  const hideTiles = columns.some((c) => c.title === 'Free Tools');
  const isFreeTools = hideTiles;

  return (
    <>
      <noscript>
        <style>{`
          .mega-menu-fallback {
            left: 50% !important;
            transform: translateX(-50%) !important;
            visibility: visible !important;
          }
        `}</style>
      </noscript>
      
      <div 
        className="mega-menu-fallback"
        style={{
          visibility: position.visibility,
          maxWidth: isFreeTools ? '1100px' : '1024px',
          margin: '0 auto'
        }}
      >
        <div 
          className={`bg-white rounded-lg shadow-lg border border-gray-200 transition-all ease-out
            ${isClosing
            ? 'opacity-0 -translate-y-1 scale-98 duration-150'
            : isVisible 
              ? 'opacity-100 translate-y-0 scale-100 duration-300' 
              : 'opacity-0 -translate-y-2 scale-95 duration-300'
        }`}
      >
        <div className="p-6">
          {isFreeTools ? (
            <FreeToolsShowcase columns={columns as any} onItemClick={handleItemClick} open={isVisible && !isClosing} />
          ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* Left: Vertical Tab Navigation */}
            <div className="col-span-3">
              <div className="space-y-1">
                {columns.map((column, index) => (
                  <button
                    key={column.title}
                    onClick={() => handleTabClick(index)}
                    className={`w-full text-left px-4 py-3 rounded-md font-medium transition-all duration-300 ease-in-out ${
                      index === activeTabIndex
                        ? 'bg-gray-100 text-gray-900 shadow-sm transform scale-[1.02]'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:scale-[1.01]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{column.title}</span>
                      <ArrowRight className={`w-4 h-4 transition-all duration-300 ${
                        index === activeTabIndex
                          ? 'text-[#294bfd] translate-x-1'
                          : 'text-gray-400'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Center: Active Tab Content */}
            <div className={`relative ${hideTiles ? 'col-span-9' : 'col-span-5'}`}>
              <div 
                key={activeTabIndex}
                className={`transition-all ease-in-out ${
                  isClosing
                    ? 'opacity-0 duration-150'
                    : isTransitioning
                      ? 'opacity-0 transform translate-x-2 duration-150'
                      : isVisible
                        ? 'opacity-100 transform translate-x-0 duration-300 delay-150'
                        : 'opacity-0 transform translate-x-4'
                }`}
              >
                <p className="text-xs font-bold uppercase tracking-wider text-gray-900 mb-4">
                  {activeColumn.title}
                </p>
                <ul className="space-y-1">
                  {activeColumn.items.map((item, itemIndex) => {
                    const IconComponent = item.icon ? iconMap[item.icon as keyof typeof iconMap] : null;
                    const itemDelay = isTransitioning ? 0 : 100 + (itemIndex * 80);
                    
                    return (
                      <li 
                        key={item.href}
                        className={`transition-all ease-in-out duration-300 ${
                          isClosing
                            ? 'opacity-0'
                            : isTransitioning
                              ? 'opacity-0'
                              : isVisible 
                                ? 'opacity-100 translate-x-0' 
                                : 'opacity-0 translate-x-2'
                        }`}
                        style={{
                          transitionDelay: `${itemDelay}ms`
                        }}
                      >
                        <Link 
                          href={item.href}
                          onClick={handleItemClick}
                          className="group block px-3 py-2 rounded-md hover:bg-gray-50 transition-all duration-200 hover:scale-[1.02] hover:shadow-sm"
                        >
                          <div className="flex items-start">
                            {IconComponent && (
                              <IconComponent 
                                className="w-5 h-5 text-[#294bfd] mt-0.5 mr-3 flex-shrink-0 transition-all duration-200 group-hover:scale-110 group-hover:text-[#ff34a7]" 
                                aria-hidden="true"
                              />
                            )}
                            <div className="flex-1">
                              <span className="font-medium text-gray-900 block transition-colors duration-200 group-hover:text-[#294bfd]">
                                {item.label}
                              </span>
                              <span className="text-sm text-gray-600 block mt-0.5 transition-colors duration-200 group-hover:text-gray-700">
                                {item.description}
                              </span>
                            </div>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Right: Premium Showcase Tiles - Social Proof Style */}
            {!hideTiles && <div className="col-span-4">
              <div className="space-y-4">
                {PREMIUM_TILES.map((tile, index) => {
                  const TileIcon = iconMap[tile.icon as keyof typeof iconMap];
                  const tileDelay = isVisible ? 300 + (index * 100) : 0;
                  
                  return (
                    <div
                      key={tile.href}
                      className={`transition-all ease-out duration-400 ${
                        isClosing
                          ? 'opacity-0'
                          : isVisible 
                            ? 'opacity-100 translate-y-0' 
                            : 'opacity-0 translate-y-4'
                      }`}
                      style={{
                        transitionDelay: `${tileDelay}ms`
                      }}
                    >
                      <Link
                        href={tile.href}
                        onClick={handleItemClick}
                        className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:scale-[1.02] hover:shadow-md transition-all duration-200"
                      >
                        {/* Gradient accent line */}
                        <div 
                          className="h-1 rounded-t-lg"
                          style={{
                            background: 'linear-gradient(to right, #294bfd, #ff34a7)'
                          }}
                        />
                        
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <div className="flex-shrink-0">
                              {TileIcon && (
                                <TileIcon className="w-5 h-5 text-[#294bfd] mt-0.5" />
                              )}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 text-sm">
                                {tile.title}
                              </div>
                              <div className="text-sm text-gray-600 mt-0.5">
                                {tile.description}
                              </div>
                            </div>
                            
                            {/* Arrow */}
                            <div className="flex-shrink-0">
                              <ArrowRight className="w-4 h-4 text-gray-400 mt-0.5" />
                            </div>
                          </div>
                          
                          {/* Verification badge */}
                          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-100">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-emerald-500">
                              <path d="M12 2L4 7v6c0 4.41 3.59 8 8 8s8-3.59 8-8V7l-8-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span className="text-xs text-gray-500">
                              {tile.badge}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>}
          </div>
          )}
        </div>
        
        {/* CTA Bar - Identical to original */}
        {cta && (
          <div 
            className={`bg-gradient-to-r from-[#294bfd] to-[#2239e6] p-6 rounded-b-lg transition-all ease-out ${
              isClosing
                ? 'opacity-0 duration-150'
                : `duration-400 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {cta.icon && (() => {
                  const CTAIcon = iconMap[cta.icon as keyof typeof iconMap];
                  return CTAIcon && (
                    <CTAIcon 
                      className="w-6 h-6 text-white mr-4 transition-transform duration-300 hover:scale-110 hover:rotate-12" 
                      aria-hidden="true"
                    />
                  );
                })()}
                <div>
                  <p className="text-lg font-semibold text-white">
                    {cta.headline}
                  </p>
                  <p className="text-sm text-blue-100 mt-0.5">
                    {cta.subtext}
                  </p>
                </div>
              </div>
              <Link
                href={cta.href}
                onClick={handleItemClick}
                className="btn-primary group !rounded-full"
              >
                Book Strategy Call
                <ArrowRight className="w-4 h-4 arrow-icon" />
              </Link>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
}